import { Logger } from 'src/common/services/Logger';

import { JiraTicketCreatedCallback } from '../TicketsSystems.types';

interface HandleJiraTicketCreatedParams {
	ticketId: string;
	jiraHost: string;
	onTicketCreated?: JiraTicketCreatedCallback;
}

const buildJiraTicketLink = (jiraHost: string, ticketId: string): string =>
	`${jiraHost}/browse/${ticketId}`;

/**
 * Shared post-creation flow for a Jira ticket: builds the ticket link, notifies
 * the consumer through `onTicketCreated`, opens the ticket in a new tab, and
 * logs any failures along the way. Returns the built ticket link so callers can
 * reuse it (e.g. for analytics or additional UI updates).
 *
 * Modal/state side effects intentionally stay in the calling component — this
 * helper only owns the cross-cutting "we just got a Jira ticket" behaviour.
 */
export const handleJiraTicketCreated = ({
	ticketId,
	jiraHost,
	onTicketCreated
}: HandleJiraTicketCreatedParams): string | null => {
	if (!ticketId) {
		return null;
	}

	const ticketLink = buildJiraTicketLink(jiraHost, ticketId);

	if (jiraHost) {
		try {
			onTicketCreated?.(ticketId, ticketLink);
		} catch (error) {
			Logger.error('Jira ticket created callback failed', {
				ticketId,
				ticketLink,
				error
			});
		}
	} else {
		Logger.error('Cannot build Jira ticket link because Jira host is missing', {
			ticketId
		});
	}

	window.open(ticketLink, '_blank');

	return ticketLink;
};
